import { ArrowBackIos } from '@mui/icons-material';
import { Grid, MenuItem, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  AmountBlob,
  BlueButton,
  RootineTable,
  RowProps,
  TableProps,
} from '../atoms';
import {
  DnaCategoryDetails,
  Loading,
  MissingResults,
  ResultNav,
  RootineFooter,
} from '../components';
import { CHECKOUT_URLS, SHOPIFY_WEB_URL } from '../config';
import { useDnaCategoryResults } from '../config/api/queries';
import { UserDnaCategory } from '../types';
import { isMobile } from '../utils';
import { useGlobalState } from '../utils/GlobalStateProvider';

export const DnaReport = (): JSX.Element => {
  const context = useGlobalState(true);
  const [selectedCategory, setSelectedCategory] = useState<UserDnaCategory>();
  const [dnaFile, setDnaFile] = useState('');
  const dnaCategories = useDnaCategoryResults();

  useEffect(() => {
    if (context.state.user.dnatest_report) {
      setDnaFile(context.state.user.dnatest_report);
    }
  }, [context.state.user]);

  useEffect(() => {
    if (dnaCategories.data?.length && !isMobile()) {
      setSelectedCategory(dnaCategories.data[0]);
    }
  }, [dnaCategories.data]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, [selectedCategory]);

  if (dnaCategories.isLoading) {
    return <Loading fullPage />;
  }

  if (!dnaCategories.data || dnaCategories.data?.length === 0) {
    return (
      <MissingResults
        head={'Missing Rootine DNA insights'.toLocaleUpperCase()}
        subhead="Take an at-home lab test to inform your Rootine precision formula and gain access to your DNA insights."
        buttonLabel="Improve my Rootine"
        buttonLink={CHECKOUT_URLS.dna_test.link}
        foot="Learn more"
        footLink={`${SHOPIFY_WEB_URL}/pages/science`}
        useFootIcon={false}
      />
    );
  }

  const rows = (): { [name: string]: RowProps }[] => {
    return dnaCategories.data.map((category) => {
      return {
        category_name: {
          value: category.name,
          bold: true,
          center: true,
        },
        nutrients: {
          value: category.related_nutrients.join(', '),
          sx: {
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        },
        amount: {
          value: <AmountBlob text={category.amount_recommended} />,
          center: true,
        },
      };
    });
  };

  const tableProps: TableProps = {
    tableName: 'categories table',
    columns: {
      category_name: {
        name: 'CATEGORY',
        mobileSortOrder: 1,
        center: true,
      },
      nutrients: {
        name: 'NUTRIENTS',
        tooltipText: 'Nutrients in your Rootine related to this category',
        mobileSortOrder: 3,
        center: true,
      },
      amount: {
        name: 'IMPACT',
        tooltipText: 'How your DNA impacts this health category',
        mobileSortOrder: 2,
        center: true,
      },
    },
    rows: rows(),
    onClick: (i: number) => {
      setSelectedCategory(dnaCategories.data[i]);
    },
  };

  return (
    <>
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        rowSpacing={2}
        sx={{
          px: { xs: 0, md: 10 },
          marginTop: { xs: 7, md: '10' },
        }}
      >
        {selectedCategory ? (
          <>
            <Grid
              item
              container
              xs={12}
              sx={{ display: { xs: 'none', lg: 'flex' } }}
            >
              <Grid item xs={12}>
                <ResultNav />
              </Grid>
              <Grid item container xs={12}>
                <Grid item xs={12} md={5} sx={{ background: '#F0F2F9' }}>
                  <DnaCategoryDetails {...{ category: selectedCategory }} />
                </Grid>
                <Grid item container xs={12} md={7}>
                  <Grid item xs={12}>
                    <RootineTable {...tableProps} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                position: 'relative',
                background: '#F0F2F9',
                display: { md: 'inline', lg: 'none' },
              }}
            >
              <Grid item xs={12} sx={{ paddingY: 3, alignItems: 'center' }}>
                <MenuItem onClick={() => setSelectedCategory(undefined)}>
                  <ArrowBackIos />
                  <Typography
                    sx={{ cursor: 'pointer', fontSize: 16, opacity: 0.5 }}
                  >
                    {' BACK'}
                  </Typography>
                </MenuItem>
              </Grid>
              <Grid item xs={12}>
                <DnaCategoryDetails {...{ category: selectedCategory }} />
              </Grid>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12}>
              <ResultNav />
            </Grid>
            <Grid item xs={12}>
              <RootineTable {...tableProps} />
            </Grid>
          </>
        )}
        {dnaFile && (
          <Grid
            container
            item
            xs={12}
            sx={{ justifyContent: { xs: 'center', md: 'right' } }}
          >
            <BlueButton label="Download Raw Report" href={dnaFile} />
          </Grid>
        )}
      </Grid>
      <Grid item xs={12}>
        <RootineFooter />
      </Grid>
    </>
  );
};
